import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip, Paper, ToggleButton, ToggleButtonGroup, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination } from '@mui/material';
import { RestartAlt as RestartAltIcon } from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  LineChart,
  Line,
  ResponsiveContainer,
} from 'recharts';
import { useChartConfig } from './ChartConfig';

const WellsCharts = ({ 
  wellsData, 
  onSortRiskWells, 
  riskWellsSortField, 
  riskWellsSortDirection, 
  onResetRiskWells,
  riskWellsFilter,
  setRiskWellsFilter,
  resetRiskWellsFilter,
  inactiveYearsBinSize,
  setInactiveYearsBinSize,
  resetInactiveYearsBinSize,
  hiddenWellGroups,
  setHiddenWellGroups,
  resetHiddenWellGroups
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const chartConfig = useChartConfig();

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Well Group color scheme
  const wellGroupColors = {
    'ACTIVE': '#4caf50',        // Green
    'PERMIT': '#607d8b',        // Grey Blue
    'SHUT IN': '#ffc107',       // Darker Yellow
    'TA': '#2196f3',            // Blue
    'PARTIAL PLUG': '#ff9800',  // Orange
    'P&A': '#8d6e63',           // Brown
    'DRY HOLE': '#e91e63'       // Pink
  };

  // Generate data for inactive years chart
  const generateInactiveYearsData = (wellsData, binSize) => {
    let yearBins;
    
    if (binSize === '1-year') {
      // Generate 1-year bins from 1 to 20+
      yearBins = [];
      for (let i = 1; i < 20; i++) {
        yearBins.push({ min: i, max: i + 1, label: `${i}-${i + 1} years` });
      }
      yearBins.push({ min: 20, max: 999, label: '20+ years' });
    } else {
      // Default 5-year bins
      yearBins = [
        { min: 1, max: 5, label: '1-5 years' },
        { min: 5, max: 10, label: '5-10 years' },
        { min: 10, max: 15, label: '10-15 years' },
        { min: 15, max: 20, label: '15-20 years' },
        { min: 20, max: 999, label: '20+ years' }
      ];
    }

    // Try different possible column names for Well Group
    const wellGroupColumn = wellsData.length > 0 ? 
      (wellsData[0]['Well Group'] !== undefined ? 'Well Group' :
       wellsData[0].well_group !== undefined ? 'well_group' :
       wellsData[0].well_status !== undefined ? 'well_status' :
       wellsData[0].status !== undefined ? 'status' : null) : null;
    
    if (!wellGroupColumn) {
      return [];
    }

    const wellGroups = [...new Set(wellsData.map(well => well[wellGroupColumn] || 'UNKNOWN'))];
    
    // Track which well groups have data
    const wellGroupsWithData = new Set();
    
    const chartData = yearBins.map(bin => {
      const dataPoint = { yearRange: bin.label };
      
      wellGroups.forEach(group => {
        const count = wellsData.filter(well => {
          const wellGroup = well[wellGroupColumn] || 'UNKNOWN';
          const inactiveYears = well.inactive_years || 0;
          return wellGroup === group && inactiveYears >= 1 && inactiveYears >= bin.min && inactiveYears < bin.max;
        }).length;
        
        dataPoint[group] = count;
        
        // Track if this group has any data
        if (count > 0) {
          wellGroupsWithData.add(group);
        }
      });
      
      return dataPoint;
    });
    
    // Remove well groups with no data from all data points
    return chartData.map(dataPoint => {
      const filteredDataPoint = { yearRange: dataPoint.yearRange };
      wellGroupsWithData.forEach(group => {
        filteredDataPoint[group] = dataPoint[group];
      });
      return filteredDataPoint;
    });
  };

  // Generate data for plug date chart
  const generatePlugDateData = (wellsData) => {
    const yearCounts = {};
    
    wellsData.forEach(well => {
      const plugDate = well['Plug Date'] || well.plug_date || well.plugged_date;
      if (plugDate) {
        const year = new Date(plugDate).getFullYear();
        yearCounts[year] = (yearCounts[year] || 0) + 1;
      }
    });
    
    return Object.entries(yearCounts)
      .map(([year, count]) => ({ year: parseInt(year), wellCount: count }))
      .sort((a, b) => a.year - b.year);
  };

  // Generate risk wells data based on filter
  const generateRiskWellsData = (wellsData, filter) => {
    return wellsData.filter(well => {
      const inactiveYears = well.inactive_years || 0;
      const plugDate = well['Plug Date'] || well.plug_date || well.plugged_date;
      const wellGroup = well['Well Group'] || well.well_group || well.well_status || well.status || '';
      const wellGroupUpper = wellGroup.toString().toUpperCase();
      
      // Check if unplugged
      const isUnplugged = plugDate === null || plugDate === undefined || plugDate === '';
      
      // Check if Active or Permit
      const isActiveOrPermit = wellGroupUpper === 'ACTIVE' || wellGroupUpper === 'PERMIT';
      
      // Determine if well is inactive:
      // 1. Unplugged AND not Active/Permit (standard inactive wells)
      // OR
      // 2. Active/Permit BUT has inactive_years > 0 (should be considered inactive)
      const isInactive = (isUnplugged && !isActiveOrPermit) || (isActiveOrPermit && inactiveYears > 0);
      
      if (!isInactive) return false;
      
      // Apply filter based on inactive years
      if (filter === 'over_5_years') {
        return inactiveYears > 5;
      } else if (filter === 'all_inactive') {
        return true; // All inactive wells (unplugged non-Active/Permit OR Active/Permit with inactive_years > 0)
      }
      
      return false;
    });
  };

  // Sort risk wells data based on sort parameters
  const getSortedRiskWellsData = (data, sortField, sortDirection) => {
    if (!sortField) return data;
    
    return [...data].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'well_name':
          aValue = a['Well Name'] || a.well_name || a.name || '';
          bValue = b['Well Name'] || b.well_name || b.name || '';
          break;
        case 'api_number':
          aValue = a['API Number'] || a.api_number || a.api || '';
          bValue = b['API Number'] || b.api_number || b.api || '';
          break;
        case 'well_group':
          aValue = a['Well Group'] || a.well_group || a.well_status || a.status || '';
          bValue = b['Well Group'] || b.well_group || b.well_status || b.status || '';
          break;
        case 'inactive_years':
          aValue = a.inactive_years || 0;
          bValue = b.inactive_years || 0;
          break;
        case 'plugging_cost':
          aValue = a['PLUGGING_COST'] || a['plugging_cost'] || a['Plugging Cost'] || 0;
          bValue = b['PLUGGING_COST'] || b['plugging_cost'] || b['Plugging Cost'] || 0;
          break;
        default:
          return 0;
      }
      
      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === 'asc' ? comparison : -comparison;
      }
      
      // Handle numeric comparison
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      return 0;
    });
  };

  const inactiveYearsData = generateInactiveYearsData(wellsData, inactiveYearsBinSize);
  const plugDateData = generatePlugDateData(wellsData);

  // Risk wells table columns
  const riskWellsColumns = [
    {
      field: 'well_name',
      label: 'Well Name',
      sortable: true,
      render: (value, row) => row['Well Name'] || row.well_name || row.name || null
    },
    {
      field: 'api_number',
      label: 'API Number',
      sortable: true,
      render: (value, row) => row['API Number'] || row.api_number || row.api || null
    },
    {
      field: 'well_group',
      label: 'Well Group',
      sortable: true,
      render: (value, row) => row['Well Group'] || row.well_group || row.well_status || row.status || null
    },
    {
      field: 'inactive_years',
      label: 'Inactive Years',
      sortable: true,
      align: 'right',
      render: (value, row) => row.inactive_years ? row.inactive_years.toFixed(1) : null
    },
    {
      field: 'plugging_cost',
      label: 'ARO $',
      sortable: true,
      align: 'right',
      render: (value, row) => {
        const pluggingCost = row['PLUGGING_COST'] || row['plugging_cost'] || row['Plugging Cost'] || null;
        if (pluggingCost !== null && pluggingCost !== undefined && pluggingCost !== '' && 
            typeof pluggingCost === 'number' && !isNaN(pluggingCost) && pluggingCost > 0) {
          return `$${pluggingCost.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
        }
        return '-';
      }
    }
  ];

  // Check if inactive years chart has data
  const hasInactiveYearsData = inactiveYearsData.length > 0 && inactiveYearsData.some(dataPoint => {
    return Object.keys(dataPoint).some(key => key !== 'yearRange' && dataPoint[key] > 0);
  });

  // Check if risk wells have data for each filter
  const riskWellsDataOver5 = generateRiskWellsData(wellsData, 'over_5_years');
  const riskWellsDataAll = generateRiskWellsData(wellsData, 'all_inactive');
  const hasRiskWellsOver5 = riskWellsDataOver5.length > 0;
  const hasRiskWellsAll = riskWellsDataAll.length > 0;
  const hasAnyRiskWells = hasRiskWellsOver5 || hasRiskWellsAll;

  // Ensure the current filter is valid (has data)
  // If current filter has no data, switch to the other one that has data
  const validRiskWellsFilter = (() => {
    if (riskWellsFilter === 'over_5_years' && !hasRiskWellsOver5 && hasRiskWellsAll) {
      return 'all_inactive';
    }
    if (riskWellsFilter === 'all_inactive' && !hasRiskWellsAll && hasRiskWellsOver5) {
      return 'over_5_years';
    }
    return riskWellsFilter;
  })();

  // Update filter state if it's invalid
  useEffect(() => {
    if (validRiskWellsFilter !== riskWellsFilter) {
      setRiskWellsFilter(validRiskWellsFilter);
    }
  }, [validRiskWellsFilter, riskWellsFilter, setRiskWellsFilter]);

  // Use valid filter for data generation
  const riskWellsData = generateRiskWellsData(wellsData, validRiskWellsFilter);
  const sortedRiskWellsData = getSortedRiskWellsData(riskWellsData, riskWellsSortField, riskWellsSortDirection);

  return (
    <Box sx={{ mt: 2 }}>
      {/* Chart 1: Inactive Years Distribution - Only show if there's data */}
      {hasInactiveYearsData && (
        <Paper elevation={0} sx={{ p: 2, mb: 3, border: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Wells by Group vs Inactive Years
            </Typography>
          <Tooltip title="Reset Graph" arrow>
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                setHiddenWellGroups(new Set());
                resetInactiveYearsBinSize();
              }}
            >
              <RestartAltIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <ToggleButtonGroup
            value={inactiveYearsBinSize}
            exclusive
            onChange={(event, newBinSize) => {
              if (newBinSize !== null) {
                setInactiveYearsBinSize(newBinSize);
              }
            }}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                }
              }
            }}
          >
            <ToggleButton value="5-year">
              5-Year Bins
            </ToggleButton>
            <ToggleButton value="1-year">
              1-Year Bins
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <ResponsiveContainer {...chartConfig.containerProps} data-chart="wells-status">
          <BarChart 
            data={inactiveYearsData} 
            margin={chartConfig.margins}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="yearRange" />
            <YAxis />
            <RechartsTooltip />
            <Legend 
              onClick={(entry) => {
                const wellGroup = entry.dataKey;
                setHiddenWellGroups(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(wellGroup)) {
                    newSet.delete(wellGroup);
                  } else {
                    newSet.add(wellGroup);
                  }
                  return newSet;
                });
              }}
            />
            {Object.keys(inactiveYearsData[0] || {}).filter(key => key !== 'yearRange').map((wellGroup) => (
              <Bar
                key={wellGroup}
                dataKey={wellGroup}
                fill={wellGroupColors[wellGroup] || '#9e9e9e'}
                stackId="a"
                name={wellGroup === 'TA' ? 'TA' : wellGroup.toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                hide={hiddenWellGroups.has(wellGroup)}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Paper>
      )}

      {/* Chart 2: Plug Date Timeline - Only show if there's data */}
      {plugDateData.length > 0 && (
        <Paper elevation={0} sx={{ p: 2, mb: 3, border: 1, borderColor: 'divider' }}>
          <Typography variant="h6" gutterBottom>
            Wells Plugged by Year
          </Typography>
          <ResponsiveContainer {...chartConfig.containerProps} data-chart="wells-timeline">
            <LineChart 
              data={plugDateData}
              margin={chartConfig.margins}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="year" 
                type="number" 
                domain={['dataMin', 'dataMax']}
              />
              <YAxis allowDataOverflow={false} />
              <RechartsTooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="wellCount" 
                name="Well Count (Annual)"
                stroke="#8884d8" 
                strokeWidth={2}
                dot={{ fill: '#8884d8', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Risk Wells Table - Only show if there's data for at least one filter */}
      {hasAnyRiskWells && (
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 3, border: 1, borderColor: 'divider' }}         data-table-title={
          validRiskWellsFilter === 'over_5_years' ? 'Risk Wells: Unplugged and Over 5 Years Inactive' : 'Risk Wells: Unplugged and Inactive'
        } data-table-type="main">
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                {validRiskWellsFilter === 'over_5_years' ? 'Risk Wells: Unplugged and Over 5 Years Inactive' : 'Risk Wells: Unplugged and Inactive'}
              </Typography>
              <Tooltip title="Reset Table" arrow>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => {
                    onResetRiskWells();
                    resetRiskWellsFilter();
                    resetHiddenWellGroups();
                  }}
                >
                  <RestartAltIcon />
                </IconButton>
              </Tooltip>
            </Box>
            {/* Only show toggle if both options are available */}
            {hasRiskWellsOver5 && hasRiskWellsAll && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <ToggleButtonGroup
                  value={validRiskWellsFilter}
                  exclusive
                  onChange={(event, newFilter) => {
                    if (newFilter !== null) {
                      setRiskWellsFilter(newFilter);
                    }
                  }}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        }
                      }
                    }
                  }}
                >
                  <ToggleButton value="over_5_years">
                    Over 5 Years
                  </ToggleButton>
                  <ToggleButton value="all_inactive">
                    All
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}
          </Box>
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {riskWellsColumns.map((column, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      fontWeight: 'bold',
                      cursor: column.sortable ? 'pointer' : 'default',
                      userSelect: 'none',
                      textAlign: column.align || 'left',
                      '&:hover': column.sortable ? { backgroundColor: 'rgba(0, 0, 0, 0.04)' } : {}
                    }}
                    onClick={column.sortable ? () => onSortRiskWells(column.field) : undefined}
                  >
                    {column.label}
                    {column.sortable && riskWellsSortField === column.field && (
                      <Box component="span" sx={{ ml: 1 }}>
                        {riskWellsSortDirection === 'asc' ? '↑' : '↓'}
                      </Box>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                // Apply pagination
                const startIndex = page * rowsPerPage;
                const endIndex = startIndex + rowsPerPage;
                const paginatedData = sortedRiskWellsData.slice(startIndex, endIndex);

                return paginatedData.map((row, index) => (
                  <TableRow key={startIndex + index}>
                    {riskWellsColumns.map((column, colIndex) => (
                      <TableCell
                        key={colIndex}
                        sx={{ textAlign: column.align || 'left' }}
                      >
                        {column.render ? column.render(row[column.field], row) : row[column.field]}
                      </TableCell>
                    ))}
                  </TableRow>
                ));
              })()}
            </TableBody>
          </Table>
        </TableContainer>
        
              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={sortedRiskWellsData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Rows:"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                }
                sx={{
                  '& .MuiTablePagination-toolbar': {
                    justifyContent: 'center',
                  },
                  '& .MuiTablePagination-spacer': {
                    display: 'none',
                  },
                }}
              />
        
        {/* Footnotes for All Inactive view */}
        {validRiskWellsFilter === 'all_inactive' && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.primary" sx={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
              <strong>Note:</strong>
            </Typography>
            <Typography variant="body2" color="text.primary" sx={{ mt: 1, fontSize: '0.75rem', fontStyle: 'italic', pl: 0, textIndent: '-1.2em', paddingLeft: '1.2em' }}>
              1. If a well is listed in the table and the INACTIVE YEARS value is NULL, then the well has been inactive for less than 1 year.
            </Typography>
            <Typography variant="body2" color="text.primary" sx={{ mt: 0.5, fontSize: '0.75rem', fontStyle: 'italic', pl: 0, textIndent: '-1.2em', paddingLeft: '1.2em' }}>
              2. If a well is listed in the table and designated as PLUGGED (e.g., the Well Group is P&A or Dry Hole), then the well record is missing the PLUG DATE.
            </Typography>
          </Box>
        )}
        </Paper>
      )}
    </Box>
  );
};

export default WellsCharts;